/**
 * useCANVMSSocket Hook
 *
 * Manages Socket.IO connection to the /canvms namespace for real-time
 * VMS event notifications and site status updates.
 *
 * Features:
 * - Connects to /canvms namespace on mount
 * - Auto-reconnects with exponential backoff
 * - Subscribes to specific sites for filtered events
 * - Exposes event handlers via options
 * - Returns connection status for UI indicator
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

// ============================================
// Constants
// ============================================

/** Socket.IO namespace for CANVMS events */
const CANVMS_NAMESPACE = '/canvms';

/** Initial reconnect delay in ms */
const RECONNECT_DELAY = 3000;

/** Maximum reconnect attempts before giving up */
const MAX_RECONNECT_ATTEMPTS = 10;

// ============================================
// Types
// ============================================

/** VMS AI/detection event from server */
export interface VMSEventPayload {
  id: number;
  site_id: number;
  camera_id: number | null;
  camera_name: string | null;
  event_type: string;
  severity: string | null;
  summary: string | null;
  thumbnail_url: string | null;
  created_at: string;
}

/** VMS incident created event from server */
export interface VMSIncidentCreatedPayload {
  incident: {
    id: number;
    camera_id: number | null;
    title: string;
    incident_type: string;
    severity: string;
    status: string;
    camera_name: string | null;
    site_name: string | null;
    thumbnail_url: string | null;
    created_at: string;
  };
  endpointId: number;
}

/** VMS site status change from server */
export interface VMSStatusChangePayload {
  site_id: number;
  site_name: string;
  previous_status: string;
  new_status: string;
  last_error: string | null;
  response_time_ms: number | null;
}

/**
 * VMS event-updated payload from server.
 *
 * Emitted when an event's status changes (acknowledge, dismiss, promote, resolve).
 * Single-event endpoints send the full DB record; bulk endpoints send a subset
 * (id, site_id, status, event_type). The consumer should match by `id`.
 */
export interface VMSEventUpdatedPayload {
  id: number;
  site_id: number;
  status: string;
  event_type?: string;
  camera_id?: number | null;
  severity?: string | null;
  summary?: string | null;
  details?: string | null;
  thumbnail_url?: string | null;
  video_clip_url?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  created_at?: string;
}

/**
 * VMS incident-updated payload from server.
 *
 * Emitted when an incident's status changes (review, dispatch, resolve, close, dismiss).
 * Always wrapped as `{ incident: {...} }`.
 */
export interface VMSIncidentUpdatedPayload {
  incident: {
    id: number;
    site_id: number;
    status: string;
    severity?: string;
    title?: string;
    incident_type?: string;
    updated_at?: string;
    [key: string]: unknown;
  };
}

/**
 * Broadcast payload when an event is claimed by an operator (hard lock).
 *
 * Emitted to all subscribers via `canvms:event-claimed`.
 */
export interface VMSEventClaimedPayload {
  eventId: number;
  operatorName: string;
  claimedAt: string;
}

/**
 * Broadcast payload when an event claim is released.
 *
 * Emitted to all subscribers via `canvms:event-released`.
 */
export interface VMSEventReleasedPayload {
  eventId: number;
}

/**
 * Direct-to-requester payload when a claim is rejected (event already held).
 *
 * Emitted via `event:claim-rejected` (no `canvms:` prefix — targeted emit).
 */
export interface VMSClaimRejectedPayload {
  eventId: number;
  holder: string;
}

/** Options for useCANVMSSocket hook */
export interface UseCANVMSSocketOptions {
  /** Called when a new AI/detection event is received */
  onEvent?: (event: VMSEventPayload) => void;
  /** Called when a new incident is created (auto or manual) */
  onIncidentCreated?: (payload: VMSIncidentCreatedPayload) => void;
  /** Called when a site's status changes */
  onStatusChange?: (status: VMSStatusChangePayload) => void;
  /** Called when an existing event's status is updated (acknowledge, dismiss, promote, resolve) */
  onEventUpdated?: (event: VMSEventUpdatedPayload) => void;
  /** Called when an existing incident's status is updated (review, dispatch, resolve, close, dismiss) */
  onIncidentUpdated?: (payload: VMSIncidentUpdatedPayload) => void;
  /** Called when an event is claimed by an operator (broadcast to all subscribers) */
  onEventClaimed?: (payload: VMSEventClaimedPayload) => void;
  /** Called when an event claim is released (broadcast to all subscribers) */
  onEventReleased?: (payload: VMSEventReleasedPayload) => void;
  /** Called when the current socket's claim request is rejected (direct emit, event already held) */
  onClaimRejected?: (payload: VMSClaimRejectedPayload) => void;
  /** Called when a claim/release operation encounters an unexpected backend error (direct emit) */
  onClaimError?: (payload: { message: string }) => void;
  /** Site IDs to subscribe to (optional, subscribes to all tenant sites if not provided) */
  siteIds?: number[];
  /** Whether to enable the socket connection (default: true) */
  enabled?: boolean;
}

/** Return value from useCANVMSSocket hook */
export interface UseCANVMSSocketReturn {
  /** Whether the socket is currently connected */
  isConnected: boolean;
  /** Connection error, if any */
  connectionError: Error | null;
  /** Manual reconnect function */
  reconnect: () => void;
  /** Subscribe to specific site events */
  subscribeSite: (siteId: number) => void;
  /** Unsubscribe from specific site events */
  unsubscribeSite: (siteId: number) => void;
  /** Claim an event for review (hard lock). Emits `event:claim` to server. */
  claimEvent: (eventId: number, operatorName: string) => void;
  /** Release a previously claimed event. Emits `event:release` to server. */
  releaseEvent: (eventId: number) => void;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Constructs the Socket.IO URL following the same pattern as API URLs.
 * In production, uses the current host. In development, checks for IIS proxy.
 */
function getSocketUrl(): string {
  if (process.env.NODE_ENV === 'production') {
    return ''; // Same origin
  }

  // In development, check if we're running through IIS (port 8000)
  if (window.location.port === '8000') {
    return ''; // Same origin through IIS
  }

  // Direct React dev server - use same origin (proxied via dev server config)
  return '';
}

// ============================================
// Hook Implementation
// ============================================

/**
 * Hook to manage Socket.IO connection to /canvms namespace
 *
 * @param options - Event handlers and configuration
 * @returns Connection status and control functions
 *
 * @example
 * ```tsx
 * const { isConnected, connectionError } = useCANVMSSocket({
 *   onEvent: (event) => {
 *     toast.info(`New ${event.event_type} event on ${event.camera_name}`);
 *   },
 *   onStatusChange: (status) => {
 *     if (status.new_status === 'down') {
 *       toast.error(`Site ${status.site_name} went offline`);
 *     }
 *   },
 *   siteIds: [1, 2, 3], // Optional: subscribe to specific sites
 * });
 * ```
 */
export function useCANVMSSocket(
  options: UseCANVMSSocketOptions = {}
): UseCANVMSSocketReturn {
  const {
    onEvent,
    onIncidentCreated,
    onStatusChange,
    onEventUpdated,
    onIncidentUpdated,
    onEventClaimed,
    onEventReleased,
    onClaimRejected,
    onClaimError,
    siteIds,
    enabled = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<Error | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Serialize siteIds for stable dependency comparison
  const siteIdsKey = JSON.stringify(siteIds);

  // Store callbacks in refs to avoid reconnecting when they change
  const callbacksRef = useRef({
    onEvent,
    onIncidentCreated,
    onStatusChange,
    onEventUpdated,
    onIncidentUpdated,
    onEventClaimed,
    onEventReleased,
    onClaimRejected,
    onClaimError,
  });

  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onEvent,
      onIncidentCreated,
      onStatusChange,
      onEventUpdated,
      onIncidentUpdated,
      onEventClaimed,
      onEventReleased,
      onClaimRejected,
      onClaimError,
    };
  }, [onEvent, onIncidentCreated, onStatusChange, onEventUpdated, onIncidentUpdated, onEventClaimed, onEventReleased, onClaimRejected, onClaimError]);

  // Subscribe to a specific site
  const subscribeSite = useCallback((siteId: number) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe:site', siteId);
    }
  }, []);

  // Unsubscribe from a specific site
  const unsubscribeSite = useCallback((siteId: number) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe:site', siteId);
    }
  }, []);

  // Claim an event for review (hard lock)
  const claimEvent = useCallback((eventId: number, operatorName: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('event:claim', { eventId, operatorName });
    }
  }, []);

  // Release a previously claimed event
  const releaseEvent = useCallback((eventId: number) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('event:release', { eventId });
    }
  }, []);

  // Connect function
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }

    const socketUrl = getSocketUrl();

    const socket = io(`${socketUrl}${CANVMS_NAMESPACE}`, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: false, // We handle reconnection manually for more control
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.debug('[CANVMS/useCANVMSSocket] Connected to server');
      setIsConnected(true);
      setConnectionError(null);
      reconnectAttemptsRef.current = 0;

      // Subscribe to specific sites if provided
      if (siteIds && siteIds.length > 0) {
        socket.emit('subscribe:sites', siteIds);
      }
    });

    socket.on('disconnect', (reason) => {
      console.debug('[CANVMS/useCANVMSSocket] Disconnected:', reason);
      setIsConnected(false);

      // Auto-reconnect unless intentionally disconnected
      if (reason !== 'io client disconnect' && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current++;
        const delay = Math.min(RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1), 48000);
        console.debug(`[CANVMS/useCANVMSSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    });

    socket.on('connect_error', (err) => {
      console.error('[CANVMS/useCANVMSSocket] Connection error:', err);
      setConnectionError(new Error(err.message || 'Failed to connect to CANVMS service'));
      setIsConnected(false);
    });

    // VMS events
    socket.on('canvms:event', (data: VMSEventPayload) => {
      console.debug('[CANVMS/useCANVMSSocket] Event received:', data);
      callbacksRef.current.onEvent?.(data);
    });

    // Incident created events
    socket.on('canvms:incident-created', (data: any) => {
      console.debug('[CANVMS/useCANVMSSocket] Incident created (raw):', data);

      // Normalize payload: backend may send flat object, frontend expects { incident, endpointId }
      let normalized: VMSIncidentCreatedPayload;
      if (data?.incident && typeof data.incident === 'object') {
        // Already wrapped — use as-is
        normalized = data as VMSIncidentCreatedPayload;
      } else if (data?.id && data?.severity) {
        // Flat incident object from backend — wrap it
        normalized = {
          incident: {
            id: data.id,
            camera_id: data.camera_id ?? null,
            title: data.title || data.type || 'Unknown',
            incident_type: data.incident_type || data.type || 'unknown',
            severity: data.severity,
            status: data.status || 'new',
            camera_name: data.camera_name || null,
            site_name: data.site_name || null,
            thumbnail_url: data.thumbnail_url || null,
            created_at:
              typeof data.created_at === 'string'
                ? data.created_at
                : new Date(data.created_at).toISOString(),
          },
          endpointId: data.endpointId ?? data.endpoint_id ?? data.site_id ?? 0,
        };
      } else {
        console.warn('[CANVMS/useCANVMSSocket] Malformed incident-created payload, ignoring:', data);
        return;
      }

      callbacksRef.current.onIncidentCreated?.(normalized);
    });

    // Status change events
    socket.on('canvms:status', (data: VMSStatusChangePayload) => {
      console.debug('[CANVMS/useCANVMSSocket] Status change:', data);
      callbacksRef.current.onStatusChange?.(data);
    });

    // Event status updated (acknowledge, dismiss, promote, resolve)
    socket.on('canvms:event-updated', (data: VMSEventUpdatedPayload) => {
      console.debug('[CANVMS/useCANVMSSocket] Event updated:', data);
      if (!data?.id || !data?.site_id) {
        console.warn('[CANVMS/useCANVMSSocket] Malformed event-updated payload, ignoring:', data);
        return;
      }
      callbacksRef.current.onEventUpdated?.(data);
    });

    // Incident status updated (review, dispatch, resolve, close, dismiss)
    socket.on('canvms:incident-updated', (data: VMSIncidentUpdatedPayload) => {
      console.debug('[CANVMS/useCANVMSSocket] Incident updated (raw):', data);

      // Normalize: backend should always send { incident: {...} } but handle flat shape defensively
      let normalized: VMSIncidentUpdatedPayload;
      if (data?.incident && typeof data.incident === 'object' && data.incident.id) {
        normalized = data;
      } else if ((data as any)?.id && (data as any)?.status) {
        // Flat incident object — wrap it
        const flat = data as any;
        normalized = {
          incident: {
            id: flat.id,
            site_id: flat.site_id ?? 0,
            status: flat.status,
            severity: flat.severity,
            title: flat.title,
            incident_type: flat.incident_type,
            updated_at: flat.updated_at,
          },
        };
      } else {
        console.warn('[CANVMS/useCANVMSSocket] Malformed incident-updated payload, ignoring:', data);
        return;
      }

      callbacksRef.current.onIncidentUpdated?.(normalized);
    });

    // Event claimed (hard lock — broadcast to all subscribers)
    socket.on('canvms:event-claimed', (data: VMSEventClaimedPayload) => {
      console.debug('[CANVMS/useCANVMSSocket] Event claimed:', data);
      if (!data?.eventId || !data?.operatorName) {
        console.warn('[CANVMS/useCANVMSSocket] Malformed event-claimed payload, ignoring:', data);
        return;
      }
      callbacksRef.current.onEventClaimed?.(data);
    });

    // Event released (hard lock — broadcast to all subscribers)
    socket.on('canvms:event-released', (data: VMSEventReleasedPayload) => {
      console.debug('[CANVMS/useCANVMSSocket] Event released:', data);
      if (!data?.eventId) {
        console.warn('[CANVMS/useCANVMSSocket] Malformed event-released payload, ignoring:', data);
        return;
      }
      callbacksRef.current.onEventReleased?.(data);
    });

    // Claim rejected (direct emit to requesting socket only — no canvms: prefix)
    socket.on('event:claim-rejected', (data: VMSClaimRejectedPayload) => {
      console.debug('[CANVMS/useCANVMSSocket] Claim rejected:', data);
      if (!data?.eventId) {
        console.warn('[CANVMS/useCANVMSSocket] Malformed claim-rejected payload, ignoring:', data);
        return;
      }
      callbacksRef.current.onClaimRejected?.(data);
    });

    // Claim error (direct emit to requesting socket — unexpected backend error)
    socket.on('claim:error', (data: { message: string }) => {
      console.warn('[CANVMS/useCANVMSSocket] Claim error:', data);
      callbacksRef.current.onClaimError?.(data);
    });

    // Generic error handler
    socket.on('error', (error: { message: string }) => {
      console.error('[CANVMS/useCANVMSSocket] Error:', error.message);
      setConnectionError(new Error(error.message));
    });
  }, [siteIdsKey]);

  // Disconnect function
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    disconnect();
    setTimeout(connect, 100);
  }, [connect, disconnect]);

  // Effect to manage connection lifecycle
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    isConnected,
    connectionError,
    reconnect,
    subscribeSite,
    unsubscribeSite,
    claimEvent,
    releaseEvent,
  };
}

export default useCANVMSSocket;
